import React, { useState } from 'react';
import { Flex, Text, Card, Button, Select, Dialog, TextArea } from '@radix-ui/themes';
import { Reorder } from 'framer-motion';
import { DateTime } from 'luxon';
import { quizService } from '../../../services/quizService';
import { toast } from 'react-toastify';

const QuizEdit = ({ quiz, onSaveQuiz }) => {
  const { course, teaching_plan_id, topics, questions, selected_question_ids, lastLessonId, type } = quiz;

  const [deadline, setDeadline] = useState("1"); // Deadline in days
  const [deadlineTime, setDeadlineTime] = useState(DateTime.now().plus({ days:1 }).set({ hour: 21, minute: 0, second: 0 }));
  const [timeLimit, setTimeLimit] = useState(900); // Time limit in minutes
  const [attempts, setAttempts] = useState(1); // Number of attempts
  const [description, setDescription] = useState('');
  const [showReorderDialog, setShowReorderDialog] = useState(false);
  const [selection, setSelection] = useState(questions.map((q) => q.id)); // Selected question IDs

  const handleDeadlineChange = (days) => {
    // if (days === 0) {
    //   setDeadlineTime(null); // No deadline
    // } else {
      const deadlineDate = DateTime.now().plus({ days }).set({ hour: 21, minute: 0, second: 0 });
      setDeadlineTime(deadlineDate.toISO());
    // }
  };

  const handleSaveQuiz = async () => {
    const quizParams = {
      course_id: course.id, 
      teaching_plan_id, 
      description,
      lesson_id:lastLessonId,
      topics:topics.map(topic => topic.id), 
      questions: selected_question_ids, 
      type, 
      attempts,
      deadline: deadlineTime,
      time_limit: timeLimit
    }
    console.log('params', quizParams)
    const response = await quizService.saveQuiz(quizParams);
    console.log('save quiz response', response);
    if(response.error){
      console.log('error', response.error);
      toast.error(response.error);
    } else {
      onSaveQuiz(response);
    }
  };

  const renderQuestionCard = (question) => (
    <Card mb="2">
      <Flex direction="column" gap="2">
        <Text size="2">{question.text}</Text>
      </Flex>
    </Card>
  );

  const typeLabel = {
    'class_work': 'Class Work',
    'trial': 'Trial',
    'assessment': 'Assessment',
    'exam': 'Exam'
  };

  return (
    <Flex direction="column" gap="3">
      <Text size="2" weight="bold">Quiz Type: {typeLabel[type]}</Text>
      <Flex direction="column" gap="2">
        <Text size="1" weight="bold">Description</Text>
        <TextArea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional. Description will be shown to the student at the beginning of the quiz."
          size="2"
        />
      </Flex>
      {(type === 'class_work' || type === 'exam') && (
        <Flex direction="column" gap="3">
          {/* Deadline Field */}
          <Flex direction="column" gap="2">
            <Text size="1" weight="bold">
              Deadline {deadlineTime && DateTime.fromISO(deadlineTime).toFormat("EEE, d'th' MMM yyyy 'at' ha")}
            </Text>
            <Select.Root
              size="1"
              defaultValue="1"
              value={deadline}
              onValueChange={(value) => {
                setDeadline(value);
                if(value != 'null'){
                  handleDeadlineChange(parseInt(value, 10));
                } else {
                  setDeadlineTime(null);
                }
                }}
              >
                <Select.Trigger />
                <Select.Content>
                <Select.Item value="null">No Deadline</Select.Item>
                {DateTime.now().hour < 20 && <Select.Item value="0">Today</Select.Item>}
                <Select.Item value="1">Tomorrow</Select.Item>
                <Select.Item value="2">2 Days</Select.Item>
                <Select.Item value="3">3 Days</Select.Item>
                <Select.Item value="7">1 Week</Select.Item>
                <Select.Item value="14">2 Weeks</Select.Item>
                </Select.Content>
              </Select.Root>
              </Flex>

              {/* Time Limit Field */}
          <Flex direction="column" gap="2">
            <Text size="1" weight="bold">Time Limit (minutes)</Text>
            <Select.Root
              size="1"
              defaultValue="30"
              value={timeLimit.toString()}
              onValueChange={(value) => setTimeLimit(parseInt(value, 10))}
            >
              <Select.Trigger />
              <Select.Content>
                <Select.Item value="300">5 Minutes</Select.Item>
                <Select.Item value="600">10 Minutes</Select.Item>
                <Select.Item value="900">15 Minutes</Select.Item>
                <Select.Item value="1800">30 Minutes</Select.Item>
                <Select.Item value="3600">1 Hour</Select.Item>
              </Select.Content>
            </Select.Root>
          </Flex>

          {type === 'class_work' && (
            <Flex direction="column" gap="2">
              <Text size="1" weight="bold">Attempts</Text>
              <Select.Root
                size="1"
                defaultValue="1"
                value={attempts.toString()}
                onValueChange={(value) => setAttempts(parseInt(value, 10))}
              >
                <Select.Trigger />
                <Select.Content>
                  <Select.Item value="1">1 Attempt</Select.Item>
                  <Select.Item value="2">2 Attempts</Select.Item>
                  <Select.Item value="3">3 Attempts</Select.Item>
                </Select.Content>
              </Select.Root>
            </Flex>
          )}
        </Flex>
      )}

      {type === 'trial' || type === 'assessment' ? (
        <Text size="2">No settings required for this quiz type.</Text>
      ) : null}

      {/* Reorder Questions Dialog */}
      <Button size="2" variant="solid" onClick={() => setShowReorderDialog(true)}>
        Reorder Questions
      </Button>
      <Dialog.Root open={showReorderDialog} onOpenChange={setShowReorderDialog}>
        <Dialog.Content>
          <Text size="2" weight="bold">Reorder Questions</Text>
          <Reorder.Group axis="y" values={selection} onReorder={setSelection}>
            {selection.map((id) => {
              const question = questions.find((q) => q.id === id);
              return (
                <Reorder.Item key={id} value={id}>
                  {question ? renderQuestionCard(question) : null}
                </Reorder.Item>
              );
            })}
          </Reorder.Group>
          <Button size="2" variant="solid" onClick={() => setShowReorderDialog(false)}>
            Close
          </Button>
        </Dialog.Content>
      </Dialog.Root>

      {/* Save Quiz Button */}
      <Button size="2" variant="solid" onClick={handleSaveQuiz}>
        Save Quiz
      </Button>
    </Flex>
  );
};

export default QuizEdit;
